import { brandApi } from "../lib/axios";

/**
 * How much white-label access a brand has right now. Resolved server-side
 * from an admin override or the subscription's plan features — never sent
 * by the client.
 */
export type WhiteLabelTier = "NONE" | "BASIC" | "FULL";

/**
 * Where the brand sits in the white-label transition.
 *
 * NONE     nobody marked this brand.
 * PENDING  marked, but it hasn't chosen a portal address yet. It keeps
 *          behaving as a normal brand until it does.
 * LIVE     address chosen; the branded portal serves.
 */
export type WhiteLabelStatus = "NONE" | "PENDING" | "LIVE";

/**
 * Shape returned by `/white-label/me`. Always resolves, even for brands with
 * no entitlement, so the settings screen renders the right prompt rather than
 * handling a 404.
 */
export interface WhiteLabelStateView {
  status: WhiteLabelStatus;
  tier: WhiteLabelTier;
  capabilities: string[];
  /** Subdomain label: `<portalSlug>.reveshare.com`. Null before activation. */
  portalSlug: string | null;
  /** Logo shown in place of the Reveshare mark. A Supabase storage URL. */
  portalLogo: string | null;
  /** Full URL of the live portal, or null before activation. */
  portalUrl: string | null;
  /**
   * The domain this deploy serves portals on — `reveshare.com` in production,
   * `staging.reveshare.com` on staging. Always shown rather than hardcoded,
   * so the form cannot promise an address the environment does not serve.
   */
  portalDomain: string;
  activatedAt: string | null;
  /** Pre-fills the activation form; already checked for availability. */
  suggestedSlug: string | null;
}

export interface SlugAvailability {
  slug: string;
  available: boolean;
  /** Why not, when unavailable — safe to show verbatim. */
  reason?: string;
  /** The URL it would become, when available. */
  url?: string;
}

export const whiteLabelService = {
  async getMyConfig(): Promise<WhiteLabelStateView> {
    const { data } = await brandApi.get<WhiteLabelStateView>("/white-label/me");
    return data;
  },

  /** Live check while the brand types, so it learns before submitting. */
  async checkSlug(slug: string): Promise<SlugAvailability> {
    const { data } = await brandApi.get<SlugAvailability>(
      "/white-label/me/slug-available",
      { params: { slug } }
    );
    return data;
  },

  /**
   * Completes the transition. From here the brand's portal is live and its
   * join links move to the subdomain — `linksChanged` is what the UI keys the
   * "re-share with your affiliates" notice off.
   *
   * Because every link affiliates share depends on the address, the UI walks
   * the brand through an explicit typed confirmation before calling this.
   */
  async activate(input: {
    portalSlug: string;
    portalLogo?: string;
    /** Email existing affiliates about the new address (their accounts are untouched either way). */
    notifyAffiliates?: boolean;
  }): Promise<WhiteLabelStateView & { linksChanged: true }> {
    const { data } = await brandApi.post<
      WhiteLabelStateView & { linksChanged: true }
    >("/white-label/me/activate", input);
    return data;
  },

  /**
   * Post-activation edits. Changing the address moves links again — every
   * previously shared link breaks — so the UI gates it behind the same typed
   * confirmation activation uses.
   */
  async update(update: {
    portalSlug?: string;
    portalLogo?: string;
  }): Promise<WhiteLabelStateView> {
    const { data } = await brandApi.put<WhiteLabelStateView>(
      "/white-label/me",
      update
    );
    return data;
  },
};
